import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiClient } from '@/services/api';

interface NavItem {
  path: string;
  label: string;
  icon: JSX.Element;
}

/**
 * 顶部导航栏 - 飞书兼容布局
 * 将菜单项集成到 Header，解决与飞书侧边栏的双侧边栏冲突
 */
interface UserInfo {
  userId: string;
  userName: string;
  departmentName: string;
  avatarUrl?: string | null;
  avatarInitial: string;
}

const USER_INFO_CACHE_KEY = 'travel:user_info';

function loadCachedUserInfo(): UserInfo | null {
  try {
    const raw = localStorage.getItem(USER_INFO_CACHE_KEY);
    return raw ? (JSON.parse(raw) as UserInfo) : null;
  } catch {
    return null;
  }
}

function saveUserInfoCache(info: UserInfo) {
  try {
    localStorage.setItem(USER_INFO_CACHE_KEY, JSON.stringify(info));
  } catch {
    // localStorage 不可用时静默忽略（无痕模式等）
  }
}

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // 优先读取缓存，避免刷新页面时出现"加载中..."闪烁
  const [userInfo, setUserInfo] = useState<UserInfo>(
    () => loadCachedUserInfo() ?? {
      userId: '',
      userName: '加载中...',
      departmentName: '',
      avatarInitial: '?',
    }
  );
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiClient.getCurrentUser()
      .then(data => {
        if (data?.userName) {
          // avatarUrl 只升不降：API 降级返回 null 时，保留缓存中的上一次有效头像
          const cached = loadCachedUserInfo();
          const merged: UserInfo = {
            ...data,
            avatarUrl: data.avatarUrl ?? cached?.avatarUrl ?? null,
          };
          setUserInfo(merged);
          saveUserInfoCache(merged);
        }
      })
      .catch(() => {
        // 查询失败静默降级，保持缓存值（或默认占位）
      });
  }, []);

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      const { logoutUrl } = await apiClient.logout();
      localStorage.removeItem(USER_INFO_CACHE_KEY);
      window.location.href = logoutUrl || '/';
    } catch {
      localStorage.removeItem(USER_INFO_CACHE_KEY);
      window.location.href = '/';
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const navItems: NavItem[] = [
    {
      path: '/dashboard',
      label: '申请列表',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
    {
      path: '/travel/new',
      label: '新建申请',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
    },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">

            {/* Logo 区域 */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700
                flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="hidden sm:flex flex-col">
                <span className="text-base font-bold text-slate-800">差旅管理系统</span>
                <span className="text-xs text-slate-500 hidden md:block">来自企业效能部</span>
              </div>
            </div>

            {/* 桌面端导航菜单 */}
            <nav className="hidden md:flex items-center gap-1 ml-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                    transition-colors duration-150 cursor-pointer ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* 右侧：用户信息 + 移动端汉堡按钮 */}
            <div className="flex items-center gap-3">
              {/* 用户信息 + 下拉菜单（桌面端） */}
              <div className="hidden sm:block relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(prev => !prev)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                  aria-label="用户菜单"
                  aria-expanded={isUserMenuOpen}
                >
                  <div className="relative w-8 h-8 flex-shrink-0">
                    {userInfo.avatarUrl && (
                      <img
                        src={userInfo.avatarUrl}
                        alt={userInfo.userName}
                        className="w-8 h-8 rounded-full object-cover shadow-sm"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    )}
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-700
                      flex items-center justify-center shadow-sm ${userInfo.avatarUrl ? 'hidden' : ''}`}>
                      <span className="text-white text-xs font-semibold">{userInfo.avatarInitial}</span>
                    </div>
                  </div>
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-medium text-slate-700 leading-none">{userInfo.userName}</p>
                    {userInfo.departmentName && (
                      <p className="text-xs text-slate-400 mt-0.5">{userInfo.departmentName}</p>
                    )}
                  </div>
                  <svg className={`w-3.5 h-3.5 text-slate-400 hidden lg:block transition-transform duration-150 ${isUserMenuOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* 下拉菜单 */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50"
                    style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}>
                    {/* 用户信息行（小屏无名字时展示） */}
                    <div className="px-4 py-2.5 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-800 truncate">{userInfo.userName}</p>
                      {userInfo.departmentName && (
                        <p className="text-xs text-slate-400 truncate mt-0.5">{userInfo.departmentName}</p>
                      )}
                    </div>
                    <button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {isLoggingOut ? '退出中...' : '退出登录'}
                    </button>
                  </div>
                )}
              </div>

              {/* 移动端汉堡菜单按钮 */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                aria-label={isMobileMenuOpen ? '关闭菜单' : '打开菜单'}
              >
                <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>

          </div>
        </div>

        {/* 移动端下拉菜单 */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white">
            <nav className="px-4 py-2 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                    transition-colors cursor-pointer ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}
              {/* 移动端用户信息 + 退出登录 */}
              <div className="border-t border-slate-100 mt-1">
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="relative w-8 h-8 flex-shrink-0">
                    {userInfo.avatarUrl && (
                      <img
                        src={userInfo.avatarUrl}
                        alt={userInfo.userName}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    )}
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-700
                      flex items-center justify-center ${userInfo.avatarUrl ? 'hidden' : ''}`}>
                      <span className="text-white text-xs font-semibold">{userInfo.avatarInitial}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{userInfo.userName}</p>
                    {userInfo.departmentName && (
                      <p className="text-xs text-slate-400">{userInfo.departmentName}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {isLoggingOut ? '退出中...' : '退出登录'}
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
